import React, { useState } from "react";
import { Plus, MapPin, Home, Building2, Star, Edit3, Trash2, X, Check } from "lucide-react";
import { useUserData } from "../../hooks/useUserData";

const empty = { label: "Home", name: "", line1: "", line2: "", city: "", state: "", zip: "", country: "USA", phone: "", isDefault: false };

const Addresses = () => {
  const { addresses, addAddress, updateAddress, removeAddress, makeDefault } = useUserData();
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(empty);

  const startNew = () => { setDraft({ ...empty }); setEditing("new"); };
  const startEdit = (a) => { setDraft({ ...a }); setEditing(a.id); };
  const close = () => { setEditing(null); setDraft(empty); };

  const save = (e) => {
    e.preventDefault();
    if (editing === "new") addAddress(draft);
    else updateAddress(editing, draft);
    close();
  };

  const remove = (id) => { if (window.confirm("Remove this address?")) removeAddress(id); };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-zinc-200 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Saved Addresses</h1>
          <p className="text-sm text-zinc-500 mt-1">Add home, office or client addresses for faster checkout.</p>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors">
          <Plus className="w-4 h-4" /> Add address
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-zinc-300 py-16 text-center mt-5">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mb-4">
            <MapPin className="w-7 h-7" />
          </div>
          <div className="font-semibold text-zinc-900 text-lg">No addresses saved yet</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Add a home, office or client address to speed up checkout on every order.</p>
          <button onClick={startNew} className="mt-6 inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 rounded-lg text-xs font-bold hover:bg-rose-500 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add your first address
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {addresses.map((a) => {
            const Icon = a.label === "Office" ? Building2 : Home;
            return (
              <div key={a.id} className="bg-white rounded-2xl border border-zinc-200 p-5 relative">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                    <div>
                      <div className="font-bold text-zinc-900">{a.label}</div>
                      {a.isDefault && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-500 mt-0.5"><Star className="w-3 h-3 fill-rose-500" /> Default</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(a)} className="w-8 h-8 rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-zinc-700 inline-flex items-center justify-center transition-colors"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(a.id)} className="w-8 h-8 rounded-md hover:bg-rose-50 text-zinc-500 hover:text-rose-500 inline-flex items-center justify-center transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="text-sm text-zinc-700 mt-4 space-y-0.5">
                  <div className="font-semibold text-zinc-900">{a.name}</div>
                  <div>{a.line1}</div>
                  {a.line2 && <div>{a.line2}</div>}
                  <div>{a.city}, {a.state} {a.zip}</div>
                  <div>{a.country}</div>
                  {a.phone && <div className="text-zinc-500 pt-1">{a.phone}</div>}
                </div>
                {!a.isDefault && (
                  <button onClick={() => makeDefault(a.id)} className="mt-4 text-xs font-semibold text-rose-500 hover:underline inline-flex items-center gap-1"><Star className="w-3 h-3" /> Make default</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-zinc-900">{editing === "new" ? "Add a new address" : "Edit address"}</h3>
              <button type="button" onClick={close} className="w-8 h-8 rounded-md hover:bg-zinc-100 inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <label className="col-span-2 text-xs font-semibold text-zinc-700">
                Label
                <select value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400 bg-white">
                  <option>Home</option><option>Office</option><option>Client</option><option>Other</option>
                </select>
              </label>
              <label className="col-span-2 text-xs font-semibold text-zinc-700">Full name<input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="col-span-2 text-xs font-semibold text-zinc-700">Address line 1<input required value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="col-span-2 text-xs font-semibold text-zinc-700">Address line 2 (optional)<input value={draft.line2} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="text-xs font-semibold text-zinc-700">City<input required value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="text-xs font-semibold text-zinc-700">State<input required value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="text-xs font-semibold text-zinc-700">ZIP<input required value={draft.zip} onChange={(e) => setDraft({ ...draft, zip: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="text-xs font-semibold text-zinc-700">Country<input value={draft.country} onChange={(e) => setDraft({ ...draft, country: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="col-span-2 text-xs font-semibold text-zinc-700">Phone<input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-md text-sm outline-none focus:border-rose-400" /></label>
              <label className="col-span-2 flex items-center gap-2 text-xs text-zinc-700 mt-1 cursor-pointer">
                <input type="checkbox" checked={draft.isDefault} onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })} className="accent-rose-500" />
                Set as default shipping address
              </label>
            </div>

            <div className="flex gap-2 mt-6">
              <button type="button" onClick={close} className="flex-1 border border-zinc-200 py-2.5 rounded-lg text-sm font-semibold hover:bg-zinc-50 transition-colors">Cancel</button>
              <button className="flex-1 bg-zinc-900 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-rose-500 transition-colors inline-flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Addresses;
